/**
 * AidesWizardController — pilote /aides-assistant.html (EPIC-005).
 *
 * Orchestre :
 *  - 4 panels (étapes ACRE / date / ARE-ARCE / récap).
 *  - Conclusion automatique live à l'étape 1 (cochage motifs → ACRE
 *    status + libellés).
 *  - Calcul live de `acre_end_date` à l'étape 2 (utility `aides.js`).
 *  - Bascule des fieldsets ARE/ARCE à l'étape 3.
 *  - Récapitulatif fidèle à l'étape 4 + valeur PASS lue depuis l'API.
 *  - Submit final → POST /aides/me, skip → POST /aides/me/skip.
 *
 * Convention : 0 HTML inline en JS, classes ⊆ `notamicro.css`.
 *
 * Hydratation : `connect()` GET /aides/me → pré-remplit le wizard
 * si l'utilisateur l'a déjà complété. Le verrou date D2 est porté côté
 * UI (input passé en `readonly`), mais le serveur reste l'autorité
 * (409 si tentative de bypass).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import {
  acreRateLabel,
  computeAcreEndDate,
  formatQuarter,
  validatePayload,
} from "/js/utils/aides.js";

const PANEL_COUNT = 4;

export default class AidesWizardController extends BaseController {
  static targets = [
    "stepBtn",
    "panel",
    "prevBtn",
    "nextBtn",
    "submitBtn",
    "skipBtn",
    "skipBtnNav",
    "progress",
    "acreStatusSelect",
    "startDateInput",
    "startDateHint",
    "endDateOutput",
    "quarterOutput",
    "acreRateOutput",
    "benefitRadio",
    "areFieldset",
    "areDailyInput",
    "areOpenInput",
    "areTotalInput",
    "areRemainingInput",
    "arceFieldset",
    "arceTotalInput",
    "arceFirstDateInput",
    "arceFirstAmountInput",
    "recapAcre",
    "recapDate",
    "recapBenefit",
  ];

  current = 0;
  isInitial = true;
  state = {
    dateLocked: false,
    wizardCompleted: false,
    wizardSkipped: false,
  };

  async connect() {
    this.render();
    try {
      const data = await api.getAidesMe();
      if (data) {
        this.hydrateFromServer(data);
      }
    } catch (_e) {
      // 401 redirige déjà vers /login. Sur autre erreur on garde les
      // défauts UI et on prévient l'utilisateur.
      this.showError("Impossible de charger l'état du wizard aides.");
    }
    // Re-render après hydratation pour que les recap/conclusions
    // reflètent l'état chargé.
    this.recomputeStatus();
    this.recomputeEndDate();
    this.updateRecap();
    this.updateSkipVisibility();
  }

  // ---------------------------------------------------------------------------
  // Hydratation
  // ---------------------------------------------------------------------------

  hydrateFromServer(data) {
    this.state.dateLocked = !!data.dateLocked;
    this.state.wizardCompleted = !!data.wizardCompleted;
    this.state.wizardSkipped = !!data.wizardSkipped;

    // Étape 1 : pré-sélectionner le statut ACRE.
    if (this.hasAcreStatusSelectTarget) {
      const status = typeof data.acreStatus === "string" ? data.acreStatus : "";
      this.acreStatusSelectTarget.value
        = status === "eligible_not_requested" || status === "not_eligible"
          ? status
          : "";
    }

    // Étape 2 : date.
    if (this.hasStartDateInputTarget) {
      this.startDateInputTarget.value = data.acreStartDate ?? "";
      if (this.state.dateLocked) {
        this.startDateInputTarget.readOnly = true;
        if (this.hasStartDateHintTarget) {
          this.startDateHintTarget.textContent
            = "Date figée par l'INPI/URSSAF — modification via support uniquement.";
        }
      }
    }

    // Étape 3 : type + détails.
    const type = data.unemploymentBenefitType ?? "none";
    for (const radio of this.benefitRadioTargets) {
      radio.checked = radio.value === type;
    }
    this.toggleBenefitDetail();

    if (data.areDailyNetAmount != null && this.hasAreDailyInputTarget) {
      this.areDailyInputTarget.value = data.areDailyNetAmount;
    }
    if (data.areRightsOpeningDate && this.hasAreOpenInputTarget) {
      this.areOpenInputTarget.value = data.areRightsOpeningDate;
    }
    if (data.areTotalDaysGranted != null && this.hasAreTotalInputTarget) {
      this.areTotalInputTarget.value = data.areTotalDaysGranted;
    }
    if (data.areRemainingDaysAtMicroStart != null && this.hasAreRemainingInputTarget) {
      this.areRemainingInputTarget.value = data.areRemainingDaysAtMicroStart;
    }
    if (data.arceTotalAmount != null && this.hasArceTotalInputTarget) {
      this.arceTotalInputTarget.value = data.arceTotalAmount;
    }
    if (data.arceFirstPaymentDate && this.hasArceFirstDateInputTarget) {
      this.arceFirstDateInputTarget.value = data.arceFirstPaymentDate;
    }
    if (data.arceFirstPaymentAmount != null && this.hasArceFirstAmountInputTarget) {
      this.arceFirstAmountInputTarget.value = data.arceFirstPaymentAmount;
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation panels
  // ---------------------------------------------------------------------------

  render() {
    this.stepBtnTargets.forEach((btn, i) => {
      const li = btn.closest(".nm-wizard__step");
      if (li) {
        li.classList.toggle("is-current", i === this.current);
        li.classList.toggle("is-done", i < this.current);
      }
      if (i === this.current) btn.setAttribute("aria-current", "step");
      else btn.removeAttribute("aria-current");
      btn.disabled = i > this.current;
    });
    this.panelTargets.forEach((p, i) => {
      p.classList.toggle("is-active", i === this.current);
    });
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = this.current === 0;
    const isLast = this.current === PANEL_COUNT - 1;
    if (this.hasNextBtnTarget) this.nextBtnTarget.hidden = isLast;
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.hidden = !isLast;
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `Étape ${this.current + 1} sur ${PANEL_COUNT}`;
    }
    if (isLast) this.updateRecap();
  }

  goTo(i) {
    if (i < 0 || i >= PANEL_COUNT) return;
    this.current = i;
    this.render();
    if (!this.isInitial) {
      const panel = this.panelTargets[i];
      if (panel) {
        panel.focus({ preventScroll: true });
        panel.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    this.isInitial = false;
  }

  prev() {
    this.goTo(this.current - 1);
  }

  next() {
    this.goTo(this.current + 1);
  }

  gotoStep(event) {
    const raw = event.params?.step;
    const i = typeof raw === "number" ? raw : parseInt(raw, 10);
    if (Number.isInteger(i) && i <= this.current) {
      this.goTo(i);
    } else if (Number.isInteger(i)) {
      // Cas du bouton "Modifier" depuis le récap → on autorise
      // la navigation arrière même si i > current (c'est un clic
      // sur les boutons de step désactivés en avant, mais le récap
      // doit pouvoir revenir éditer une étape antérieure).
      this.goTo(i);
    }
  }

  // ---------------------------------------------------------------------------
  // Étape 1 : statut ACRE (saisi par l'utilisateur via select binaire)
  // ---------------------------------------------------------------------------

  selectedAcreStatus() {
    if (!this.hasAcreStatusSelectTarget) return "";
    const v = this.acreStatusSelectTarget.value;
    return v === "eligible_not_requested" || v === "not_eligible" ? v : "";
  }

  recomputeStatus() {
    this.updateRecap();
    this.updateSkipVisibility();
  }

  // ---------------------------------------------------------------------------
  // Étape 2 : calcul live `acre_end_date`
  // ---------------------------------------------------------------------------

  recomputeEndDate() {
    const value = this.hasStartDateInputTarget ? this.startDateInputTarget.value : "";
    const endIso = computeAcreEndDate(value);
    if (this.hasEndDateOutputTarget) {
      this.endDateOutputTarget.textContent
        = endIso ? this.formatDate(endIso) : "—";
    }
    if (this.hasQuarterOutputTarget) {
      this.quarterOutputTarget.textContent = formatQuarter(value);
    }
    if (this.hasAcreRateOutputTarget) {
      this.acreRateOutputTarget.textContent = acreRateLabel(value);
    }
    this.updateSkipVisibility();
  }

  // ---------------------------------------------------------------------------
  // Étape 3 : bascule ARE/ARCE
  // ---------------------------------------------------------------------------

  selectedBenefitType() {
    const radio = this.benefitRadioTargets.find((r) => r.checked);
    return radio ? radio.value : "none";
  }

  toggleBenefitDetail() {
    const type = this.selectedBenefitType();
    if (this.hasAreFieldsetTarget) {
      this.areFieldsetTarget.hidden = type !== "are";
    }
    if (this.hasArceFieldsetTarget) {
      this.arceFieldsetTarget.hidden = type !== "arce";
    }
    // Reset des champs du fieldset masqué pour cohérence.
    if (type !== "are") {
      this.clearInputs([
        this.hasAreDailyInputTarget ? this.areDailyInputTarget : null,
        this.hasAreOpenInputTarget ? this.areOpenInputTarget : null,
        this.hasAreTotalInputTarget ? this.areTotalInputTarget : null,
        this.hasAreRemainingInputTarget ? this.areRemainingInputTarget : null,
      ]);
    }
    if (type !== "arce") {
      this.clearInputs([
        this.hasArceTotalInputTarget ? this.arceTotalInputTarget : null,
        this.hasArceFirstDateInputTarget ? this.arceFirstDateInputTarget : null,
        this.hasArceFirstAmountInputTarget ? this.arceFirstAmountInputTarget : null,
      ]);
    }
    this.updateSkipVisibility();
  }

  clearInputs(inputs) {
    for (const input of inputs) {
      if (input) input.value = "";
    }
  }

  hasAnyInput() {
    if (this.selectedAcreStatus() !== "") return true;
    if (this.hasStartDateInputTarget && this.startDateInputTarget.value) return true;
    if (this.selectedBenefitType() !== "none") return true;
    return false;
  }

  updateSkipVisibility() {
    const hide = this.hasAnyInput();
    if (this.hasSkipBtnTarget) this.skipBtnTarget.hidden = hide;
    if (this.hasSkipBtnNavTarget) this.skipBtnNavTarget.hidden = hide;
  }

  // ---------------------------------------------------------------------------
  // Étape 4 : récap
  // ---------------------------------------------------------------------------

  updateRecap() {
    const status = this.selectedAcreStatus();

    if (this.hasRecapAcreTarget) {
      while (this.recapAcreTarget.firstChild) {
        this.recapAcreTarget.removeChild(this.recapAcreTarget.firstChild);
      }
      if (status === "") {
        this.recapAcreTarget.appendChild(document.createTextNode("—"));
      } else {
        const isEligible = status === "eligible_not_requested";
        const strong = document.createElement("strong");
        strong.textContent = isEligible ? "Éligible" : "Non éligible";
        strong.classList.add(isEligible ? "text-success" : "text-muted");
        this.recapAcreTarget.appendChild(strong);
        if (isEligible) {
          this.recapAcreTarget.appendChild(document.createElement("br"));
          const span = document.createElement("span");
          span.classList.add("muted", "fs-12");
          span.textContent
            = "Demande à transmettre à l'URSSAF dans les 45 jours suivant la création — sinon éligibilité perdue.";
          this.recapAcreTarget.appendChild(span);
        }
      }
    }

    if (this.hasRecapDateTarget) {
      while (this.recapDateTarget.firstChild) {
        this.recapDateTarget.removeChild(this.recapDateTarget.firstChild);
      }
      const startIso = this.hasStartDateInputTarget ? this.startDateInputTarget.value : "";
      if (startIso) {
        this.recapDateTarget.appendChild(
          document.createTextNode(this.formatDate(startIso)),
        );
        const endIso = computeAcreEndDate(startIso);
        if (endIso) {
          this.recapDateTarget.appendChild(document.createElement("br"));
          const span = document.createElement("span");
          span.classList.add("muted", "fs-12");
          span.textContent = `Fin ACRE estimée : ${this.formatDate(endIso)}`;
          this.recapDateTarget.appendChild(span);
        }
      } else {
        this.recapDateTarget.appendChild(document.createTextNode("—"));
      }
    }

    if (this.hasRecapBenefitTarget) {
      while (this.recapBenefitTarget.firstChild) {
        this.recapBenefitTarget.removeChild(this.recapBenefitTarget.firstChild);
      }
      const type = this.selectedBenefitType();
      if (type === "none") {
        this.recapBenefitTarget.appendChild(document.createTextNode("Aucune allocation"));
      } else if (type === "are") {
        const strong = document.createElement("strong");
        strong.textContent = "ARE mensuelle";
        this.recapBenefitTarget.appendChild(strong);
        const daily = this.hasAreDailyInputTarget ? this.areDailyInputTarget.value : "";
        const remaining = this.hasAreRemainingInputTarget
          ? this.areRemainingInputTarget.value : "";
        if (daily) {
          this.recapBenefitTarget.appendChild(
            document.createTextNode(` · ${daily} €/jour`),
          );
        }
        if (remaining) {
          this.recapBenefitTarget.appendChild(
            document.createTextNode(` · ${remaining} j de reliquat`),
          );
        }
      } else {
        const strong = document.createElement("strong");
        strong.textContent = "ARCE en capital";
        this.recapBenefitTarget.appendChild(strong);
        const total = this.hasArceTotalInputTarget ? this.arceTotalInputTarget.value : "";
        if (total) {
          this.recapBenefitTarget.appendChild(
            document.createTextNode(` · total prévu ${total} €`),
          );
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Submit / skip
  // ---------------------------------------------------------------------------

  buildPayload() {
    const startDate = this.hasStartDateInputTarget && this.startDateInputTarget.value
      ? this.startDateInputTarget.value
      : null;
    const type = this.selectedBenefitType();
    const payload = {
      acreStatus: this.selectedAcreStatus() || "not_eligible",
      unemploymentBenefitType: type,
    };
    if (startDate) payload.acreStartDate = startDate;
    if (type === "are") {
      if (this.hasAreDailyInputTarget && this.areDailyInputTarget.value) {
        payload.areDailyNetAmount = this.areDailyInputTarget.value;
      }
      if (this.hasAreOpenInputTarget && this.areOpenInputTarget.value) {
        payload.areRightsOpeningDate = this.areOpenInputTarget.value;
      }
      if (this.hasAreTotalInputTarget && this.areTotalInputTarget.value) {
        payload.areTotalDaysGranted = parseInt(this.areTotalInputTarget.value, 10);
      }
      if (this.hasAreRemainingInputTarget && this.areRemainingInputTarget.value) {
        payload.areRemainingDaysAtMicroStart
          = parseInt(this.areRemainingInputTarget.value, 10);
      }
    } else if (type === "arce") {
      if (this.hasArceTotalInputTarget && this.arceTotalInputTarget.value) {
        payload.arceTotalAmount = this.arceTotalInputTarget.value;
      }
      if (this.hasArceFirstDateInputTarget && this.arceFirstDateInputTarget.value) {
        payload.arceFirstPaymentDate = this.arceFirstDateInputTarget.value;
      }
      if (this.hasArceFirstAmountInputTarget && this.arceFirstAmountInputTarget.value) {
        payload.arceFirstPaymentAmount = this.arceFirstAmountInputTarget.value;
      }
    }
    return payload;
  }

  async submit() {
    const payload = this.buildPayload();
    const errors = validatePayload(payload);
    if (Object.keys(errors).length > 0) {
      this.showError(`Validation échouée : ${Object.values(errors).join(", ")}`);
      return;
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.textContent = "Enregistrement…";
      this.submitBtnTarget.disabled = true;
    }
    try {
      const data = await api.submitAides(payload);
      this.hydrateFromServer(data);
      this.showSuccess("Profil enregistré.");
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.textContent = "Enregistré ✓";
      }
    } catch (e) {
      // Le serveur peut renvoyer 409 (date locked) ou 422 (validation).
      const detail = e?.data?.error?.details?.errors;
      if (detail && typeof detail === "object") {
        this.showError(
          "Validation serveur : " + Object.values(detail).join(", "),
        );
      } else if (e?.status === 409) {
        this.showError(
          "Date figée par l'INPI/URSSAF. Modification via support uniquement.",
        );
      } else {
        this.showError("Impossible d'enregistrer le wizard aides.");
      }
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.textContent = "Enregistrer mon profil";
        this.submitBtnTarget.disabled = false;
      }
    }
  }

  async skip() {
    try {
      await api.skipAides();
      this.showSuccess("Vous pourrez compléter votre profil plus tard.");
      window.location.assign("/tableau-de-bord-prospect.html");
    } catch (_e) {
      this.showError("Impossible d'enregistrer votre choix.");
    }
  }

}
